import { useCallback, useMemo, useState } from "react";
import { ActivityIndicator, ScrollView } from "react-native";
import { Stack, useLocalSearchParams, useRouter } from "expo-router";
import { useUnistyles } from "react-native-unistyles";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { View, Banner, Card } from "@src/components";
import { CameraThumbnailCard, cameraThumbnailGridStyles } from "@src/features/cameras/components/CameraThumbnailCard";
import { useSiteCameraKeys } from "@src/features/cameras/hooks/useSiteCameraKeys";
import { getCameraKeyString } from "@src/utils/key-parser";
import { FloatingSelectionBar } from "@src/features/settings/components/FloatingSelectionBar";
import { useAlarmsByCameras } from "@src/services/api/hooks";
import { useHeaderBackButton } from "@src/hooks/useHeaderBackButton";
import { useSitePermissions } from "@visioni/a3s-access/app";
import { buildAlarmSettingsRoutes } from "@src/features/alarms/utils/alarm-settings-routes";
import { groupCamerasByRoutine, RoutineGroupHeader, useRoutineMap, type RoutineGroup } from "./routine-shared";

export default function ManageRoutines() {
  const { theme } = useUnistyles();
  const { bottom } = useSafeAreaInsets();
  const router = useRouter();
  const { tenantId, siteId } = useLocalSearchParams<{ tenantId: string; siteId: string }>();
  const { siteCameras, cameraKeys } = useSiteCameraKeys(siteId);
  const { canManageAllAlarmSchedules, isLoading: permissionsLoading } = useSitePermissions(siteId, cameraKeys);
  const permissionsResolved = !permissionsLoading;
  const { headerLeft } = useHeaderBackButton();
  const routes = useMemo(() => buildAlarmSettingsRoutes(tenantId, siteId), [tenantId, siteId]);

  const [selectedCameraKeys, setSelectedCameraKeys] = useState<Set<string>>(new Set());
  const alarmQueries = useAlarmsByCameras(siteId, cameraKeys);

  const isLoadingAlarms = alarmQueries.some((q) => q.isLoading);

  const routineMap = useRoutineMap(siteCameras, alarmQueries);

  const routineGroups = useMemo(() => groupCamerasByRoutine(siteCameras, routineMap), [siteCameras, routineMap]);

  const toggleCamera = useCallback((cameraKey: string) => {
    setSelectedCameraKeys((prev) => {
      const next = new Set(prev);
      if (next.has(cameraKey)) {
        next.delete(cameraKey);
      } else {
        next.add(cameraKey);
      }
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback((group: RoutineGroup) => {
    const groupKeys = group.cameras.map((c) => getCameraKeyString(c));
    setSelectedCameraKeys((prev) => {
      const allSelected = groupKeys.every((key) => prev.has(key));
      const next = new Set(prev);
      if (allSelected) {
        groupKeys.forEach((key) => next.delete(key));
      } else {
        groupKeys.forEach((key) => next.add(key));
      }
      return next;
    });
  }, []);

  const handleChangeRoutine = useCallback(() => {
    if (selectedCameraKeys.size === 0 || !canManageAllAlarmSchedules) return;
    router.push({
      pathname: routes.changeRoutine as never,
      params: { cameraKeys: JSON.stringify(Array.from(selectedCameraKeys)) },
    });
  }, [selectedCameraKeys, canManageAllAlarmSchedules, router, routes]);

  return (
    <>
      <Stack.Screen options={{ title: "Manage routines", headerLeft }} />
      <ScrollView
        style={{ flex: 1, backgroundColor: theme.colors.background }}
        contentContainerStyle={{
          paddingHorizontal: theme.spacing["3xl"],
          paddingTop: theme.spacing["4xl"],
          paddingBottom: bottom + theme.spacing["6xl"],
          flexGrow: 1,
        }}
        showsVerticalScrollIndicator={false}
      >
        <Banner
          title={
            !permissionsResolved || canManageAllAlarmSchedules ?
              "Select from the cameras below to change their routines."
            : "You don't have permission to manage alarm schedules for all cameras at this site."
          }
          variant={permissionsResolved && !canManageAllAlarmSchedules ? "warning" : "info"}
        />

        {isLoadingAlarms && (
          <View mt="7xl" alignItems="center">
            <ActivityIndicator color={theme.colors.text} />
          </View>
        )}

        <View flex={1} flexDirection="row" style={{ flexWrap: "wrap" }} gap="3xl" mt="3xl">
          {!isLoadingAlarms &&
            routineGroups.map((group) => {
              const groupKeys = group.cameras.map((c) => getCameraKeyString(c));
              const allSelected = groupKeys.every((key) => selectedCameraKeys.has(key));

              return (
                <Card key={group.key} style={{ flex: 1, minWidth: 300 }}>
                  <RoutineGroupHeader
                    routine={group.routine}
                    allSelected={allSelected}
                    onSelectAll={
                      permissionsResolved && canManageAllAlarmSchedules ? () => toggleSelectAll(group) : () => {}
                    }
                  />
                  <View style={cameraThumbnailGridStyles.grid} mt="4xl">
                    {group.cameras.map((camera) => {
                      const cameraKey = getCameraKeyString(camera);
                      const isSelected = selectedCameraKeys.has(cameraKey);

                      return (
                        <View key={cameraKey} style={cameraThumbnailGridStyles.gridItem}>
                          <CameraThumbnailCard
                            camera={camera}
                            isSelected={isSelected}
                            onToggle={
                              permissionsResolved && canManageAllAlarmSchedules ?
                                () => toggleCamera(cameraKey)
                              : () => {}
                            }
                          />
                        </View>
                      );
                    })}
                  </View>
                </Card>
              );
            })}
        </View>
      </ScrollView>
      {permissionsResolved && canManageAllAlarmSchedules && (
        <FloatingSelectionBar
          selectedCount={selectedCameraKeys.size}
          actionLabel="Change routine"
          onAction={handleChangeRoutine}
          bottomInset={bottom}
        />
      )}
    </>
  );
}
